import { useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { contactsApi, interactionsApi } from '@/lib/api';
import { EmailHistory } from '@/components/emails/EmailHistory';
import { Building2, Edit3, Mail, Plus, Star, Users } from 'lucide-react';
import {
  Button,
  Card,
  EmptyState,
  Kontakt,
  Skeleton,
  StatusPill,
  toast,
} from '@elbklar/ui';
import { formatApiError } from '@/lib/validation';
import { herkunftAus, herkunftRueckweg, mitHerkunft } from '@/lib/herkunft';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { Timeline } from '@/features/interactions/components/Timeline';
import { InteractionSheet } from '@/features/interactions/components/InteractionSheet';

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Mittleres Glied der Kette (siehe lib/herkunft.ts): Diese Seite erbt die
  // Herkunft von der Kundenseite und gibt sie ans Formular weiter. Ohne das
  // fuehrte der Weg zurueck immer in die allgemeine Kontaktliste — auch von
  // einem Kunden, bei dem man gerade noch war.
  const herkunft = herkunftAus(searchParams);
  const rueckweg = herkunftRueckweg(herkunft) ?? { to: '/contacts', label: 'Zur Kontaktliste' };
  const [settingPrimary, setSettingPrimary] = useState(false);
  const [showInteractionModal, setShowInteractionModal] = useState(false);

  const { data: contact, isLoading } = useQuery({
    queryKey: ['contacts', id],
    queryFn: () => contactsApi.get(id!),
    enabled: !!id,
  });

  const { data: interactions } = useQuery({
    queryKey: ['interactions', { contact_id: id }],
    queryFn: () => interactionsApi.list({ contact_id: id }),
    enabled: !!id,
  });

  const handleSetPrimary = async () => {
    if (!id || contact?.is_primary || settingPrimary) return;
    try {
      setSettingPrimary(true);
      await contactsApi.setPrimary(id);
      // Abwarten: Der Knopf läuft, bis „Primärer Kontakt“ dasteht.
      await queryClient.invalidateQueries({ queryKey: ['contacts'] });
    } catch (error) {
      toast.error(formatApiError(error) || 'Kontakt ließ sich nicht als primär setzen');
    } finally {
      setSettingPrimary(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4 pt-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-48 rounded-lg" />
      </div>
    );
  }

  if (!contact) {
    return (
      <>
        <CrmPageHeader title="Kontakt" zurueck={rueckweg} />
        <EmptyState
          icon={Users}
          title="Kontakt nicht gefunden"
          action={<Button onClick={() => navigate(rueckweg.to)}>{rueckweg.label}</Button>}
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      <CrmPageHeader
        size="page"
        zurueck={rueckweg}
        title={`${contact.first_name} ${contact.last_name}`}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
            {contact.is_primary && (
              <StatusPill tone="highlight">
                <Star size={12} strokeWidth={1.75} aria-hidden />
                Primärer Kontakt
              </StatusPill>
            )}
            {contact.role && <span>{contact.role}</span>}
          </span>
        }
        mehrAktionen={[
          ...(!contact.is_primary
            ? [{ label: 'Als primär setzen', icon: Star, onSelect: handleSetPrimary, loading: settingPrimary }]
            : []),
          { label: 'Bearbeiten', icon: Edit3, onSelect: () => navigate(mitHerkunft(`/contacts/${id}/edit`, herkunft)) },
        ]}
      />

      {/* Am Handy zuerst: anrufen oder schreiben, mit einem Tipp */}
      <Kontakt darstellung="knoepfe" telefon={contact.phone} mobil={contact.mobile} email={contact.email} className="md:hidden" />

      {/* Info Cards */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2" title="Kontaktdaten" icon={Mail}>
          {contact.email || contact.phone || contact.mobile ? (
            <Kontakt email={contact.email} telefon={contact.phone} mobil={contact.mobile} />
          ) : (
            <p className="t-small text-muted-foreground">Keine Kontaktdaten hinterlegt</p>
          )}
        </Card>

        {/* Ein Kontakt haengt an einem Kunden oder an einem Partner; die
            Partnerseite war bis zum 22.10.2026 gar nicht erreichbar (Audit C2). */}
        <Card title="Zugehörig zu" icon={Building2}>
          <div className="space-y-3">
            {contact.customer_id && (
              <Link
                to={`/customers/${contact.customer_id}`}
                className="flex items-center gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-accent"
              >
                <Building2 size={20} strokeWidth={1.75} className="text-highlight" aria-hidden />
                <div>
                  <div className="font-medium">{contact.customer_name || 'Kunde'}</div>
                  <div className="t-small text-muted-foreground">Kunde</div>
                </div>
              </Link>
            )}
            {contact.partner_id && (
              <Link
                to={`/partners/${contact.partner_id}`}
                className="flex items-center gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-accent"
              >
                <Users size={20} strokeWidth={1.75} className="text-highlight" aria-hidden />
                <div>
                  <div className="font-medium">{contact.partner_name || 'Partner'}</div>
                  <div className="t-small text-muted-foreground">Partner</div>
                </div>
              </Link>
            )}
            {!contact.customer_id && !contact.partner_id && (
              <p className="text-muted-foreground">Weder Kunde noch Partner zugeordnet</p>
            )}
          </div>
        </Card>
      </div>

      {/* Notes */}
      {contact.notes && (
        <Card title="Notizen">
          <p className="whitespace-pre-wrap">{contact.notes}</p>
        </Card>
      )}

      {/* Email History */}
      <EmailHistory contactId={id} />

      {/* Interactions Timeline */}
      <Card
        title={`Interaktionen (${interactions?.total ?? 0})`}
        actions={
          <Button size="sm" icon={Plus} onClick={() => setShowInteractionModal(true)}>
            Interaktion anlegen
          </Button>
        }
      >
        <Timeline interactions={interactions?.items ?? []} />
      </Card>

      <InteractionSheet
        open={showInteractionModal}
        onOpenChange={setShowInteractionModal}
        title="Interaktion anlegen"
        description={`Mit ${contact.first_name} ${contact.last_name}`}
        contactId={id}
        customerId={contact.customer_id ?? undefined}
        partnerId={contact.customer_id ? undefined : (contact.partner_id ?? undefined)}
      />
    </div>
  );
}
